import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { spacesApi, vehicleTypesApi, parkingApi, searchApi, settingsApi, revenueApi, exitApi } from '../services/api';
import { formatCurrency, formatDuration, calculateElapsedMinutes, getAlertLevel, formatApiError, formatDateTime } from '../utils/helpers';
import CheckInModal from '../components/CheckInModal';
import CheckOutModal from '../components/CheckOutModal';
import SearchModal from '../components/SearchModal';
import ReprintModal from '../components/ReprintModal';
import ReceiptPreview from '../components/ReceiptPreview';
import { useRealtimeSync, ConnectionIndicator } from '../hooks/useRealtimeSync';
import { 
  SignOut, MagnifyingGlass, Printer, Gear, CarSimple, Motorcycle, Bicycle,
  Clock, CurrencyCircleDollar, WarningCircle, TrendUp, ShoppingCart, ArrowCounterClockwise, X
} from '@phosphor-icons/react';

const VEHICLE_ICONS = {
  'Auto': CarSimple,
  'Moto': Motorcycle,
  'Bicicleta': Bicycle
};

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_parkdesk-pro/artifacts/ty0wykz2_R6uvV.jpg';

export default function OperatorKiosk() {
  const { user, logout, isAdmin } = useAuth();
  const [spaces, setSpaces] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [settings, setSettings] = useState({});
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [showCheckOut, setShowCheckOut] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showReprint, setShowReprint] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [livePrices, setLivePrices] = useState({});
  const [dailyTotal, setDailyTotal] = useState(null);
  const [showHeaderDetail, setShowHeaderDetail] = useState(false);
  const [lastExit, setLastExit] = useState(null); // { transaction_id, plate, space_label, expiresAt }
  const [undoing, setUndoing] = useState(false);
  const intervalRef = useRef(null);
  const dailyTotalIntervalRef = useRef(null);

  // ── Real-time sync via SSE ──
  const refreshSpacesAndPrices = useCallback(() => {
    loadDataSilent();
    updateDailyTotal();
  }, []);

  const { status: realtimeStatus } = useRealtimeSync({
    enabled: !!user,
    onSpaceUpdate: refreshSpacesAndPrices,
    onChargeUpdate: refreshSpacesAndPrices,
    onConfigUpdate: (entityType) => {
      if (entityType === 'settings') loadDataSilent();
      if (entityType === 'vehicle_types') loadDataSilent();
    },
    onSystemReset: () => { loadDataSilent(); updateDailyTotal(); }
  });

  const loadData = useCallback(async () => {
    try {
      const [spacesRes, typesRes, settingsRes] = await Promise.all([
        spacesApi.getAll(),
        vehicleTypesApi.getAll(),
        settingsApi.get()
      ]);
      setSpaces(spacesRes.data);
      setVehicleTypes(typesRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Silent reload (no loading spinner) — for SSE-triggered updates
  const loadDataSilent = useCallback(async () => {
    try {
      const [spacesRes, typesRes, settingsRes] = await Promise.all([
        spacesApi.getAll(),
        vehicleTypesApi.getAll(),
        settingsApi.get()
      ]);
      setSpaces(spacesRes.data);
      setVehicleTypes(typesRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error silent reload:', error);
    }
  }, []);

  // Load live daily total
  const updateDailyTotal = useCallback(async () => {
    try {
      const res = await revenueApi.getLiveDailyTotal();
      setDailyTotal(res.data);
    } catch (error) {
      console.error('Error loading daily total:', error);
    }
  }, []);

  // Load live prices for occupied spaces
  const updateLivePrices = useCallback(async () => {
    const occupiedSpaces = spaces.filter(s => s.status === 'occupied');
    const pricePromises = occupiedSpaces.map(async (space) => {
      try {
        const res = await parkingApi.calculatePrice(space.id);
        return { id: space.id, ...res.data };
      } catch {
        return { id: space.id, price: 0, minutes: 0, service_charges: [] };
      }
    });
    
    const results = await Promise.all(pricePromises);
    const pricesMap = {};
    results.forEach(r => {
      pricesMap[r.id] = { price: r.price, minutes: r.minutes, service_charges: r.service_charges || [] };
    });
    setLivePrices(pricesMap);
  }, [spaces]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Update live prices every 10 seconds (SSE handles instant updates, this is fallback)
  useEffect(() => {
    if (spaces.length > 0) {
      updateLivePrices();
      intervalRef.current = setInterval(updateLivePrices, 10000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [spaces, updateLivePrices]);

  // Update daily total every 15 seconds (SSE handles instant updates)
  useEffect(() => {
    updateDailyTotal();
    dailyTotalIntervalRef.current = setInterval(updateDailyTotal, 15000);
    return () => {
      if (dailyTotalIntervalRef.current) clearInterval(dailyTotalIntervalRef.current);
    };
  }, [updateDailyTotal]);

  const handleSpaceClick = (space) => {
    setSelectedSpace(space);
    if (space.status === 'available') {
      setShowCheckIn(true);
    } else {
      setShowCheckOut(true);
    }
  };

  const handleCheckInComplete = (ticketData) => {
    setPrintData({ type: 'entry', data: ticketData });
    setShowCheckIn(false);
    setSelectedSpace(null);
    loadData();
    updateDailyTotal(); // Update total immediately
  };

  const handleCheckOutComplete = (ticketData) => {
    setPrintData({ type: 'exit', data: ticketData });
    setShowCheckOut(false);
    setSelectedSpace(null);
    loadData();
    updateDailyTotal(); // Update total immediately
    // Show "Deshacer salida" banner for 10 minutes
    if (ticketData?.transaction_id) {
      setLastExit({
        transaction_id: ticketData.transaction_id,
        plate_number: ticketData.plate_number,
        space_label: ticketData.space_label,
        total_price: ticketData.total_price,
        expiresAt: Date.now() + 10 * 60 * 1000,
      });
    }
  };

  // Auto-hide undo banner when it expires
  useEffect(() => {
    if (!lastExit) return;
    const remaining = lastExit.expiresAt - Date.now();
    if (remaining <= 0) { setLastExit(null); return; }
    const t = setTimeout(() => setLastExit(null), remaining);
    return () => clearTimeout(t);
  }, [lastExit]);

  const handleUndoExit = async () => {
    if (!lastExit || undoing) return;
    if (!window.confirm(`¿Deshacer salida de ${lastExit.plate_number} (espacio ${lastExit.space_label})?\nLa transacción de ${formatCurrency(lastExit.total_price)} será anulada.`)) return;
    setUndoing(true);
    try {
      await exitApi.undo(lastExit.transaction_id, 'operator_error');
      setLastExit(null);
      loadData();
      updateDailyTotal();
    } catch (err) {
      window.alert(formatApiError(err));
    } finally {
      setUndoing(false);
    }
  };

  const handleVoidComplete = () => {
    setShowCheckOut(false);
    setSelectedSpace(null);
    loadData();
    updateDailyTotal(); // Update total immediately
  };

  const handleSearchSelect = (space) => {
    setShowSearch(false);
    setSelectedSpace(space);
    setShowCheckOut(true);
  };

  const getVehicleTypeData = (typeName) => {
    return vehicleTypes.find(vt => vt.name === typeName) || {};
  };

  // Calculate service charge summary for a space
  const getServiceChargeSummary = (spaceId) => {
    const liveData = livePrices[spaceId];
    if (!liveData || !liveData.service_charges) return null;
    
    const charges = liveData.service_charges.filter(c => !c.voided);
    if (charges.length === 0) return null;
    
    const paidCharges = charges.filter(c => c.payment_mode === 'paid_now');
    const pendingCharges = charges.filter(c => c.payment_mode === 'add_to_total');
    
    // Build item list with quantities for display
    const items = charges.map(c => ({
      name: c.item_name || c.service_type || 'Extra',
      qty: c.quantity || 1,
      amount: c.amount || 0,
      mode: c.payment_mode
    }));
    
    return {
      paidAmount: paidCharges.reduce((sum, c) => sum + (c.amount || 0), 0),
      pendingAmount: pendingCharges.reduce((sum, c) => sum + (c.amount || 0), 0),
      items
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-white text-xl">Cargando...</div>
      </div>
    );
  }

  const occupiedCount = spaces.filter(s => s.status === 'occupied').length;
  const availableCount = spaces.filter(s => s.status === 'available').length;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-[#141414] border-b border-[#27272A] px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="flex items-center gap-3">
              <img 
                src={LOGO_URL} 
                alt="Glandymar" 
                className="w-10 h-10 rounded-lg object-contain bg-white/10"
              />
              <div>
                <h1 className="text-lg font-heading text-white" data-testid="business-name">{settings.business_name || 'GLANDYMAR'}</h1>
                <p className="text-sm text-[#A1A1AA]">{user?.name} - {user?.role === 'admin' ? 'Admin' : 'Operador'}</p>
              </div>
            </div>

            {/* Live Financial Header — Cobrado / Pendiente / Total */}
            {dailyTotal && (
              <div className="hidden md:flex items-center gap-2 relative" data-testid="daily-total-container">
                <button onClick={() => setShowHeaderDetail(!showHeaderDetail)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#052E16] border border-[#16A34A]/30 rounded-lg hover:bg-[#052E16]/80 transition-colors cursor-pointer"
                  data-testid="cobrado-btn">
                  <div className="flex flex-col">
                    <span className="text-[#4ADE80] font-mono text-base font-bold leading-tight" data-testid="cobrado-value">
                      {formatCurrency(dailyTotal.cobrado || 0)}
                    </span>
                    <span className="text-[#4ADE80]/70 text-[9px] leading-tight uppercase">Cobrado</span>
                  </div>
                </button>
                <button onClick={() => setShowHeaderDetail(!showHeaderDetail)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#422006] border border-[#D97706]/30 rounded-lg hover:bg-[#422006]/80 transition-colors cursor-pointer"
                  data-testid="pendiente-btn">
                  <div className="flex flex-col">
                    <span className="text-[#FCD34D] font-mono text-base font-bold leading-tight" data-testid="pendiente-value">
                      {formatCurrency(dailyTotal.pendiente || 0)}
                    </span>
                    <span className="text-[#FCD34D]/70 text-[9px] leading-tight uppercase">Pendiente</span>
                  </div>
                </button>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1C1C1E] border border-[#27272A] rounded-lg">
                  <TrendUp className="w-4 h-4 text-white" weight="bold" />
                  <div className="flex flex-col">
                    <span className="text-white font-mono text-base font-bold leading-tight" data-testid="daily-total-value">
                      {formatCurrency(dailyTotal.total_del_dia)}
                    </span>
                    <span className="text-white/50 text-[9px] leading-tight uppercase">Total</span>
                  </div>
                </div>
                {/* Expandable breakdown */}
                {showHeaderDetail && dailyTotal.cobrado_detail && (
                  <div className="absolute top-full right-0 mt-2 bg-[#141414] border border-[#27272A] rounded-xl p-4 shadow-2xl z-50 min-w-[260px]"
                    data-testid="header-detail-panel">
                    <div className="space-y-3">
                      <div>
                        <p className="text-[#4ADE80] text-xs font-bold uppercase mb-1">Cobrado Hoy</p>
                        <div className="flex justify-between text-sm">
                          <span className="text-[#A1A1AA]">Estacionamiento</span>
                          <span className="text-white font-mono">{formatCurrency(dailyTotal.cobrado_detail.estacionamiento)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[#A1A1AA]">Servicios/Productos</span>
                          <span className="text-white font-mono">{formatCurrency(dailyTotal.cobrado_detail.servicios_productos)}</span>
                        </div>
                      </div>
                      <div className="border-t border-[#27272A] pt-2">
                        <p className="text-[#FCD34D] text-xs font-bold uppercase mb-1">Pendiente</p>
                        <div className="flex justify-between text-sm">
                          <span className="text-[#A1A1AA]">Estacionamiento activo</span>
                          <span className="text-white font-mono">{formatCurrency(dailyTotal.pendiente_detail?.estacionamiento_activo || 0)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[#A1A1AA]">Extras pendientes</span>
                          <span className="text-white font-mono">{formatCurrency(dailyTotal.pendiente_detail?.extras_pendientes || 0)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Mobile Financial Summary */}
            {dailyTotal && (
              <div className="flex md:hidden items-center gap-1.5" data-testid="daily-total-mobile">
                <span className="text-[#4ADE80] font-mono text-xs font-bold px-1.5 py-0.5 bg-[#052E16] rounded">
                  {formatCurrency(dailyTotal.cobrado || 0)}
                </span>
                <span className="text-[#FCD34D] font-mono text-xs font-bold px-1.5 py-0.5 bg-[#422006] rounded">
                  {formatCurrency(dailyTotal.pendiente || 0)}
                </span>
              </div>
            )}

            {/* Stats */}
            <div className="hidden sm:flex items-center gap-4 mr-2">
              <ConnectionIndicator status={realtimeStatus} />
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#16A34A]"></div>
                <span className="text-white font-mono" data-testid="available-count">{availableCount}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#DC2626]"></div>
                <span className="text-white font-mono" data-testid="occupied-count">{occupiedCount}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <button
              onClick={() => setShowSearch(true)}
              className="bg-[#27272A] hover:bg-[#3f3f46] text-white p-3 rounded-lg transition-colors"
              data-testid="search-btn"
            >
              <MagnifyingGlass className="w-6 h-6" weight="bold" />
            </button>
            <button
              onClick={() => setShowReprint(true)}
              className="bg-[#27272A] hover:bg-[#3f3f46] text-white p-3 rounded-lg transition-colors"
              data-testid="reprint-btn"
            >
              <Printer className="w-6 h-6" weight="bold" />
            </button>
            {isAdmin && (
              <a
                href="/admin"
                className="bg-[#27272A] hover:bg-[#3f3f46] text-white p-3 rounded-lg transition-colors"
                data-testid="admin-btn"
              >
                <Gear className="w-6 h-6" weight="bold" />
              </a>
            )}
            <button
              onClick={logout}
              className="bg-[#450A0A] hover:bg-[#7F1D1D] text-[#FCA5A5] p-3 rounded-lg transition-colors"
              data-testid="logout-btn"
            >
              <SignOut className="w-6 h-6" weight="bold" />
            </button>
          </div>
        </div>
      </header>

      {/* Quick Undo Banner — appears for 10 min after a checkout */}
      {lastExit && (
        <div className="bg-gradient-to-r from-[#422006] to-[#1C1917] border-b border-[#D97706]/40 px-4 py-2.5 flex items-center justify-between gap-3" data-testid="undo-exit-banner">
          <div className="flex items-center gap-3 min-w-0">
            <ArrowCounterClockwise className="w-5 h-5 text-[#FCD34D] shrink-0" weight="bold" />
            <div className="text-sm min-w-0">
              <span className="text-[#FCD34D] font-bold">Salida reciente:</span>
              <span className="text-white ml-2 truncate">
                {lastExit.plate_number} · espacio {lastExit.space_label} · {formatCurrency(lastExit.total_price)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleUndoExit}
              disabled={undoing}
              className="px-3 py-1.5 bg-[#D97706] hover:bg-[#F59E0B] disabled:opacity-50 text-white text-sm font-bold rounded-lg transition-colors"
              data-testid="undo-exit-btn"
            >
              {undoing ? 'Deshaciendo...' : 'Deshacer salida'}
            </button>
            <button
              onClick={() => setLastExit(null)}
              className="p-1.5 text-[#A1A1AA] hover:text-white"
              data-testid="undo-exit-dismiss-btn"
              aria-label="Cerrar"
            >
              <X className="w-4 h-4" weight="bold" />
            </button>
          </div>
        </div>
      )}

      {/* Parking Grid */}
      <main className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {spaces.map((space) => {
            const isOccupied = space.status === 'occupied';
            const alertLevel = isOccupied ? getAlertLevel(space.vehicle?.entry_time) : 'normal';
            const liveData = livePrices[space.id] || {};
            const VehicleIcon = isOccupied ? VEHICLE_ICONS[space.vehicle?.vehicle_type] || CarSimple : null;
            const serviceChargeSummary = isOccupied ? getServiceChargeSummary(space.id) : null;

            let spotClass = 'spot-available';
            if (isOccupied) {
              if (alertLevel === 'critical') spotClass = 'spot-critical';
              else if (alertLevel === 'alert') spotClass = 'spot-alert';
              else spotClass = 'spot-occupied';
            }

            return (
              <button
                key={space.id}
                onClick={() => handleSpaceClick(space)}
                className={`${spotClass} border-2 rounded-xl p-4 text-left transition-all hover:scale-[1.02] active:scale-[0.98]`}
                data-testid={`spot-${space.label}`}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl font-heading">{space.label}</span>
                  <div className="flex items-center gap-1">
                    {isOccupied && VehicleIcon && (
                      <VehicleIcon className="w-6 h-6" weight="duotone" />
                    )}
                    {alertLevel !== 'normal' && (
                      <WarningCircle className="w-5 h-5 text-[#FCD34D]" weight="fill" />
                    )}
                  </div>
                </div>

                {isOccupied ? (
                  <>
                    {/* Plate Number */}
                    <div className="font-mono text-2xl sm:text-3xl tracking-[0.1em] uppercase font-bold mb-2" data-testid={`plate-${space.label}`}>
                      {space.vehicle?.plate_number}
                    </div>

                    {/* Service Charge Indicator */}
                    {serviceChargeSummary && (
                      <div className="mb-2 space-y-1">
                        {serviceChargeSummary.paidAmount > 0 && (
                          <div className="flex items-center gap-1 text-xs text-[#4ADE80]" data-testid={`extras-paid-${space.label}`}>
                            <ShoppingCart className="w-3 h-3" weight="bold" />
                            <span>Cobrado: {formatCurrency(serviceChargeSummary.paidAmount)}</span>
                          </div>
                        )}
                        {serviceChargeSummary.pendingAmount > 0 && (
                          <div className="flex items-center gap-1 text-xs text-[#FCD34D]" data-testid={`extras-pending-${space.label}`}>
                            <ShoppingCart className="w-3 h-3" weight="bold" />
                            <span>Pendiente: {formatCurrency(serviceChargeSummary.pendingAmount)}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Time & Price */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-sm opacity-80">
                        <Clock className="w-4 h-4" weight="bold" />
                        <span className="font-mono">{formatDuration(liveData.minutes || calculateElapsedMinutes(space.vehicle?.entry_time))}</span>
                      </div>
                      <div className="font-mono text-xl sm:text-2xl font-bold" data-testid={`price-${space.label}`}>
                        {formatCurrency(liveData.price || 0)}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <span className="text-lg opacity-60">DISPONIBLE</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </main>

      {/* Modals */}
      {showCheckIn && selectedSpace && (
        <CheckInModal
          space={selectedSpace}
          vehicleTypes={vehicleTypes}
          onClose={() => { setShowCheckIn(false); setSelectedSpace(null); }}
          onComplete={handleCheckInComplete}
        />
      )}

      {showCheckOut && selectedSpace && (
        <CheckOutModal
          space={selectedSpace}
          vehicleTypes={vehicleTypes}
          onClose={() => { setShowCheckOut(false); setSelectedSpace(null); }}
          onComplete={handleCheckOutComplete}
          onVoidComplete={handleVoidComplete}
        />
      )}

      {showSearch && (
        <SearchModal
          onClose={() => setShowSearch(false)}
          onSelect={handleSearchSelect}
        />
      )}

      {showReprint && (
        <ReprintModal
          onClose={() => setShowReprint(false)}
          onPrint={(data) => { setPrintData(data); setShowReprint(false); }}
        />
      )}

      {printData && (
        <ReceiptPreview
          data={printData}
          settings={settings}
          onClose={() => setPrintData(null)}
        />
      )}
    </div>
  );
}
